import type { SesionRenovada } from '@pasos/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { Boton } from '../components/ui/Boton.js';
import { Campo } from '../components/ui/Campo.js';
import { PACIENTE } from '../copy/index.js';
import { ErrorDeApi } from '../lib/api.js';
import { guardarSesion } from '../lib/sesion.js';
import { INICIO_DEL_ROL } from '../rutas/sesion.js';
import * as api from './api.js';

/**
 * Entrar con el código de activación, en la app instalada (INGRESO-2).
 *
 * Va **siempre** debajo de "Para entrar necesitás tu enlace", en cualquier
 * dispositivo: no se intenta detectar si la app está instalada. Así sirve igual
 * a la app del iPhone (que no tiene la cookie de Safari), a un celular nuevo y a
 * quien abre PASOS+ en otro navegador.
 *
 * El error es uno solo para todo lo que el servidor rechaza: no se dice si el
 * código venció, si ya se usó o si nunca existió. El freno tiene su frase,
 * porque ahí lo que hay que hacer es otra cosa: esperar.
 */

/** Los códigos se tipean con espacios a veces: sólo cuentan los dígitos. */
function soloDigitos(texto: string): string {
  return texto.replace(/\D/g, '').slice(0, 6);
}

function fraseDelError(error: unknown): string {
  if (error instanceof ErrorDeApi && error.estado === 429) return PACIENTE.acceso.codigoFrenado;
  if (error instanceof ErrorDeApi && error.estado < 500) return PACIENTE.acceso.codigoNoSirve;
  return PACIENTE.acceso.codigoSinRespuesta;
}

export function IngresoConCodigo({
  canjear = api.canjearCodigo,
}: {
  /** Inyectable: en los tests no hay servidor. */
  canjear?: (codigo: string) => Promise<SesionRenovada>;
}) {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const idDelTitulo = useId();
  const [codigo, setCodigo] = useState('');

  const canje = useMutation({
    mutationFn: canjear,
    onSuccess: (sesion) => {
      guardarSesion({ idToken: sesion.idToken, rol: sesion.rol });
      // Lo que se sabía sin sesión (el `/yo` que falló) ya no vale.
      cliente.clear();
      navegar(INICIO_DEL_ROL[sesion.rol], { replace: true });
    },
    onError: () => setCodigo(''),
  });

  function alEnviar(evento: FormEvent) {
    evento.preventDefault();
    if (codigo.length === 6) canje.mutate(codigo);
  }

  return (
    <form
      aria-labelledby={idDelTitulo}
      className="flex w-full flex-col gap-5 rounded-xl bg-card p-6 text-left text-heading"
      noValidate
      onSubmit={alEnviar}
    >
      <div className="flex flex-col gap-2">
        <h2 className="m-0 font-serif text-2xl font-regular" id={idDelTitulo}>
          {PACIENTE.acceso.codigoRotulo}
        </h2>
        <p className="m-0 text-lg text-body">{PACIENTE.acceso.codigoDetalle}</p>
      </div>

      <Campo
        autoComplete="one-time-code"
        className="text-display-xs tracking-widest tabular-nums"
        etiqueta={PACIENTE.acceso.codigoEtiqueta}
        inputMode="numeric"
        maxLength={7}
        name="codigo"
        onChange={(evento) => setCodigo(soloDigitos(evento.target.value))}
        pattern="[0-9]*"
        required
        value={codigo}
      />

      {canje.isError ? (
        <p
          className="m-0 rounded-md bg-destructive-bg p-4 text-md font-semi text-destructive-fg"
          role="alert"
        >
          {fraseDelError(canje.error)}
        </p>
      ) : null}

      <Boton cargando={canje.isPending} disabled={codigo.length !== 6} type="submit">
        {PACIENTE.acceso.codigoEntrar}
      </Boton>
    </form>
  );
}
